import * as React from "react"
import styled from "styled-components"
import { HEADER_HEIGHT_L, INNER_MAX_WIDTH } from "../../constants"
import { useKeyHandler } from "../../hooks/useKeyHandler"
import { breakpoint, colors, fontWeight, scale } from "../../styles"
import { Flex, LayoutProps } from "../Flex"
import { Icon } from "../Icon"

export interface PrimaryMenuItemProps {
	as?: any
}

export interface PrimaryMenuItemDropdownProps {
	component: React.ReactNode
	dropdownId: string
}

interface PrimaryMenuDropdownProps {
	id: string
	offsetTop?: number
	children?: React.ReactNode
}

interface PrimaryMenuComponent {
	Inner: React.FC
	Item: React.FC<PrimaryMenuItemProps>
	ItemDropdown: React.FC<PrimaryMenuItemDropdownProps>
	Dropdown: React.FC<PrimaryMenuDropdownProps>
	CloseDropdownToggle: React.FC<React.HTMLProps<HTMLButtonElement>>
}

export interface PrimaryMenuItemContextValue {
	activeDropdownIds: string[]
	openDropdown: (dropdownId: string) => void
	closeDropdown: (dropdownId: string) => void
	closeAllDropdowns: () => void
	dropdownRefs?: React.RefObject<React.RefObject<HTMLElement>[]>
}

export const PrimaryMenuContext =
	React.createContext<PrimaryMenuItemContextValue>({
		activeDropdownIds: [],
		openDropdown: (dropdownId) => { },
		closeDropdown: (dropdownId) => { },
		closeAllDropdowns: () => { },
	})

const PrimaryMenuDropdown = styled.div<
	PrimaryMenuDropdownProps & { isActive: boolean }
>`
	display: flex;
	justify-content: center;
	position: absolute;
	top: ${(props) => scale.px(props.offsetTop || HEADER_HEIGHT_L)};
	left: 0;
	width: 100%;
	background-color: ${colors["white"]};
	box-shadow: 0px ${scale.px(1)} ${scale.px(2)} rgba(0, 0, 0, 0.05);
	height: ${(props) => (props.isActive ? "auto" : 0)};
	visibility: ${(props) => (props.isActive ? "visible" : "hidden")};
	opacity: ${(props) => (props.isActive ? "1" : "0")};
	transition: visibility 0.3s, opacity 0.3s;
	overflow: hidden;
	z-index: 1;
`

const PrimaryMenuDropdownOverlay = styled.div`
	position: absolute;
	width: 100%;
	height: 100vh;
	top: 100%;
	left: 0;
	background: rgba(0, 0, 0, 0.3);
	opacity: 0.3;
`

const PrimaryMenuItemStyled = styled.li<PrimaryMenuItemProps>`
	position: relative;
	color: ${colors["navy"]};
	font-weight: ${fontWeight["bold"]};
	font-size: ${scale.px(2)};
	line-height: ${scale.px(4)};
	padding-bottom: 2px;
	white-space: nowrap;

	${breakpoint("xl")`
		font-size: ${scale.px(2)};
	`}

	&:after {
		content: "";
		position: absolute;
		bottom: 0;
		left: 0;
		width: 100%;
		height: 2px;
		background-color: ${colors["navy"]};
		visibility: hidden;
		transform: scaleX(0);
		transition: all 0.1s ease-in-out;
	}

	&:hover,
	button[aria-expanded="true"] & {
		&:after {
			visibility: visible;
			transform: scaleX(1);
		}
	}
`

export const PrimaryMenuChevronStyled = styled(Icon)`
	transition: transform 0.2s cubic-bezier(0.7, 0, 0.84, 0);
	transform: rotate(0);

	button[aria-expanded="true"] & {
		transform: rotate(-180deg);
	}
`

const PrimaryMenuContainerStyled = styled.ul`
	display: flex;
	justify-content: flex-start;
	margin-top: ${scale.px(1)};
	width: 100%;
	column-gap: ${scale.px(2)};

	${breakpoint("l")`
	margin-top: 0;
	`}

	${breakpoint("xl")`
		justify-content: space-between;
	`}
`

const CloseDropdownToggleStyled = styled.button`
	position: absolute;
	top: ${scale.px(5)};
	right: ${scale.px(4)};
	opacity: 0;
	font-size: ${scale.px(2)};
	text-transform: uppercase;
	text-decoration: underline;

	&.focus-visible {
		opacity: 1;
	}
`

const PrimaryMenu: React.FC<LayoutProps> & PrimaryMenuComponent = ({
	zIndex,
	...rest
}) => {
	const [activeDropdownIds, setActiveDropdownIds] = React.useState<string[]>([])
	const closeDropdown = (dropdownId: string) => {
		if (activeDropdownIds.includes(dropdownId)) {
			setActiveDropdownIds((prev) => prev.filter((id) => id !== dropdownId))
		}
	}
	const dropdownRefs = React.useRef<React.MutableRefObject<HTMLElement>[]>([])

	// close on click outside
	React.useEffect(() => {
		const outsideClickListener = (e: MouseEvent) => {
			if (dropdownRefs.current) {
				const clickOutside = dropdownRefs.current.every(
					(elementRef) =>
						!elementRef.current?.contains(e.target as Document) === true
				)
				if (clickOutside && activeDropdownIds.length > 0) {
					closeAllDropdowns()
				}
			}
		}

		document.addEventListener("click", outsideClickListener, false)

		return () => {
			document.removeEventListener("click", outsideClickListener, false)
		}
	}, [activeDropdownIds, dropdownRefs, setActiveDropdownIds])

	const openDropdown = (dropdownId: string) => {
		closeAllDropdowns()
		if (!activeDropdownIds.includes(dropdownId)) {
			setActiveDropdownIds((prev) => {
				return [...prev, dropdownId]
			})
		}
	}

	const closeAllDropdowns = React.useCallback(() => {
		setActiveDropdownIds([])
	}, [setActiveDropdownIds])

	// close on esc key
	useKeyHandler(27, closeAllDropdowns)

	return (
		<PrimaryMenuContext.Provider
			value={{
				activeDropdownIds,
				closeDropdown,
				openDropdown,
				closeAllDropdowns,
				dropdownRefs,
			}}
		>
			<Flex zIndex={zIndex} w={1} flex={1}>
				<Flex
					as="nav"

					justifyContent="center"
					backgroundColor="white"
					{...rest}
				/>
			</Flex>
		</PrimaryMenuContext.Provider>
	)
}

PrimaryMenu.Inner = (props) => <PrimaryMenuContainerStyled {...props} />

PrimaryMenu.Item = (props) => <PrimaryMenuItemStyled {...props} />

PrimaryMenu.ItemDropdown = (({ component, ...rest }) => {
	const { openDropdown, closeDropdown, activeDropdownIds, dropdownRefs } =
		React.useContext(PrimaryMenuContext)
	const elementRef = React.useRef<HTMLLIElement>(null)

	React.useEffect(() => {
		if (dropdownRefs?.current && elementRef?.current) {
			dropdownRefs.current.push(elementRef)
		}
	}, [elementRef, dropdownRefs])

	const toggleDropdown = () => {
		activeDropdownIds.includes(rest.dropdownId)
			? closeDropdown(rest.dropdownId)
			: openDropdown(rest.dropdownId)
	}

	return (
		<li ref={elementRef}>
			<Flex
				as="button"
				alignItems="center"
				onClick={toggleDropdown}
				aria-controls={rest.dropdownId}
				aria-expanded={
					activeDropdownIds.indexOf(rest.dropdownId) > -1 ? true : false
				}
			>
				{component}
				<PrimaryMenuChevronStyled
					icon="chevron-menu-down"
					size={2}
					mt={0.25}
					ml={0.375}
				/>
			</Flex>
		</li>
	)
}) as React.FC<PrimaryMenuItemDropdownProps>

PrimaryMenu.Dropdown = (({ children, ...rest }) => {
	const { activeDropdownIds, dropdownRefs } =
		React.useContext(PrimaryMenuContext)
	const dropdownRef = React.useRef<HTMLDivElement>(null)

	React.useEffect(() => {
		if (dropdownRefs?.current && dropdownRef?.current) {
			dropdownRefs.current.push(dropdownRef)
		}
	}, [dropdownRef, dropdownRefs])
	return (
		<PrimaryMenuDropdown
			isActive={activeDropdownIds.includes(rest.id)}
			{...rest}
		>
			<Flex ref={dropdownRef} w={1} maxw={INNER_MAX_WIDTH} py={5} mx={3}>
				{children}
			</Flex>
			<PrimaryMenuDropdownOverlay />
		</PrimaryMenuDropdown>
	)
}) as React.FC<PrimaryMenuDropdownProps>

PrimaryMenu.CloseDropdownToggle = (props) => (
	<CloseDropdownToggleStyled>{props.children}</CloseDropdownToggleStyled>
)

export { PrimaryMenu }
